import { useCallback, useContext, useRef } from 'react';
import { PortfolioContext } from '../../contexts/PortfolioContext.tsx';
import { FIXED_GAME_CONFIG } from '../../constants/fixedGameConfig.ts';
import { tileToIndex } from '../../simulation/tileUtils.ts';

type DragMode = 'select' | 'deselect';

function getCellFromPoint(x: number, y: number): { row: number; col: number } | null {
  const el = document.elementFromPoint(x, y)?.closest('[data-grid-row]');
  if (!el) return null;

  const row = el.getAttribute('data-grid-row');
  const col = el.getAttribute('data-grid-col');
  if (row === null || col === null) return null;

  return { row: Number(row), col: Number(col) };
}

export default function PortfolioGrid() {
  const portfolio = useContext(PortfolioContext);
  const dragModeRef = useRef<DragMode | null>(null);
  const lastCellRef = useRef<string | null>(null);

  const applyAt = useCallback(
    (row: number, col: number, mode: DragMode) => {
      if (!portfolio) return;

      const key = `${row},${col}`;
      if (lastCellRef.current === key) return;
      lastCellRef.current = key;

      portfolio.setTileSelected(row, col, mode === 'select');
    },
    [portfolio]
  );

  const endDrag = useCallback(() => {
    dragModeRef.current = null;
    lastCellRef.current = null;
  }, []);

  if (!portfolio) return null;

  const { grid, phase, selectedTiles } = portfolio;
  const selectedSet = new Set(selectedTiles);
  const canSelect = phase === 'selecting';
  const gridSize = FIXED_GAME_CONFIG.gridSize;

  const handlePointerDown = (row: number, col: number) => (e: React.PointerEvent) => {
    if (!canSelect || e.button !== 0) return;

    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

    const index = tileToIndex(row, col, gridSize);
    dragModeRef.current = selectedSet.has(index) ? 'deselect' : 'select';
    lastCellRef.current = null;
    applyAt(row, col, dragModeRef.current);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!canSelect || !dragModeRef.current) return;

    const cell = getCellFromPoint(e.clientX, e.clientY);
    if (cell) applyAt(cell.row, cell.col, dragModeRef.current);
  };

  return (
    <div
      className={`grid-container portfolio-grid${canSelect ? ' portfolio-grid--selecting' : ''}`}
      style={{ gridTemplateColumns: `repeat(${gridSize}, 1fr)` }}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onLostPointerCapture={endDrag}
    >
      {grid.map((row, rowIndex) =>
        row.map((cellState, colIndex) => {
          const index = tileToIndex(rowIndex, colIndex, gridSize);
          const isSelected = selectedSet.has(index);
          const isSelectable = canSelect && cellState === 'alive';

          return (
            <div
              key={`${rowIndex}-${colIndex}`}
              data-grid-row={rowIndex}
              data-grid-col={colIndex}
              className={[
                'cell',
                `cell-${cellState}`,
                isSelected ? 'cell-portfolio-selected' : '',
                isSelectable ? 'cell-portfolio-selectable' : 'cell-disabled',
              ]
                .filter(Boolean)
                .join(' ')}
              onPointerDown={handlePointerDown(rowIndex, colIndex)}
              role={isSelectable ? 'button' : undefined}
              aria-pressed={isSelectable ? isSelected : undefined}
              aria-label={
                isSelected
                  ? `Tile ${index}, selected`
                  : `Tile ${index}${isSelectable ? ', click or drag to select' : ''}`
              }
            />
          );
        })
      )}
    </div>
  );
}
